import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import {
	canDeleteComment,
	COMMENT_DELETE_FORBIDDEN,
} from "@productlane/lib/src/browser"

import type { MutatorTx, Schema } from "../schema"
import type {
	CreateCommentArgs,
	ResolveAllCommentsArgs,
	SetCommentResolvedArgs,
} from "../types"
import type { DeleteID, UpdateValue } from "@rocicorp/zero"

interface BulkResolveCandidate {
	parentId?: string | null
	userId?: string | null
	linearCommentId?: string | null
	isResolved?: boolean | null
	isDeleted?: boolean | null
}

// Device-info and AI-feedback rows carry no author and no Linear mirror, so they
// render as system events and are not comment threads anyone can resolve.
export function isBulkResolvableComment(comment: BulkResolveCandidate) {
	if (comment.parentId) return false
	if (comment.isDeleted) return false
	if (comment.isResolved) return false
	return Boolean(comment.userId ?? comment.linearCommentId)
}

export const commentMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateCommentArgs
			ctx: ZeroJwtPayload
		}) => {
			await tx.mutate.comments.insert({
				...args,
				createdAt: Date.now(),
				updatedAt: Date.now(),
				isDeleted: false,
				isResolved: false,
				version: 1,
				workspaceId: authData.workspaceId,
			})
		},
	),
	update: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: UpdateValue<Schema["tables"]["comments"]>
		}) => {
			await tx.mutate.comments.update({ id: args.id, content: args.content })
		},
	),
	setResolved: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: SetCommentResolvedArgs }) => {
			// Resolving the root comment resolves the whole comment thread, so a
			// reply cannot end up resolved while its parent is not.
			const [target] = await tx.query.comments.where("id", args.id).run()
			const rootId = target?.parentId ?? args.id
			await tx.mutate.comments.update({
				id: rootId,
				isResolved: args.isResolved,
				updatedAt: Date.now(),
			})
		},
	),
	resolveAllInThread: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: ResolveAllCommentsArgs }) => {
			const comments = await tx.query.comments
				.where("feedbackId", args.feedbackId)
				.run()
			for (const comment of comments) {
				if (!isBulkResolvableComment(comment)) continue
				await tx.mutate.comments.update({
					id: comment.id,
					isResolved: true,
					updatedAt: Date.now(),
				})
			}
		},
	),
	delete: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: DeleteID<Schema["tables"]["comments"]>
			ctx: ZeroJwtPayload
		}) => {
			// Deleting a comment removes its whole reply subtree.
			const [target] = await tx.query.comments.where("id", args.id).run()
			if (
				target &&
				!canDeleteComment({
					commentUserId: target.userId,
					actorUserId: authData.userId,
					role: authData.role,
				})
			) {
				throw new Error(COMMENT_DELETE_FORBIDDEN)
			}
			const idsToDelete = new Set<string>([args.id])
			if (target) {
				const siblings = await tx.query.comments
					.where("feedbackId", target.feedbackId)
					.run()
				let changed = true
				while (changed) {
					changed = false
					for (const c of siblings) {
						if (
							c.parentId &&
							idsToDelete.has(c.parentId) &&
							!idsToDelete.has(c.id)
						) {
							idsToDelete.add(c.id)
							changed = true
						}
					}
				}
			}
			for (const id of idsToDelete) {
				await tx.mutate.comments.update({ id, isDeleted: true })
			}
		},
	),
}
